import { useCallback, useEffect, useState } from 'react';
import { SearchGroupContractMembersResponse } from 'api/contract';
import { contractsAPI } from 'services/swagger-api';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';

/** Aligné sur la borne serveur (`Range(1, 100)` sur `pageSize`). */
export const PAGE_SIZE = 20;

const SEARCH_DEBOUNCE_MS = 400;

export interface GroupMembersPaymentsQuery {
  searchString: string;
  blockedOnly: boolean;
  pageNumber: number;
}

export interface GroupMembersPayments {
  data: SearchGroupContractMembersResponse | null;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  query: GroupMembersPaymentsQuery;
  /** Saisie en cours : la requête ne suit qu'après la temporisation. */
  searchInput: string;
  setSearchInput: (value: string) => void;
  setBlockedOnly: (value: boolean) => void;
  setPageNumber: (value: number) => void;
}

/**
 * Collectifs et leurs aliments, avec l'état de prélèvement de chacun.
 *
 * La recherche est temporisée : chaque frappe déclencherait sinon une requête qui agrège
 * les échéances de tous les aliments de la page, soit le plus coûteux des appels de
 * l'écran. Tout changement de filtre ramène à la première page — rester en page 4 d'un
 * résultat qui n'en compte plus qu'une affiche une liste vide sans rien expliquer.
 */
export const useGroupMembersPayments = (): GroupMembersPayments => {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState<GroupMembersPaymentsQuery>({
    searchString: '',
    blockedOnly: false,
    pageNumber: 1,
  });

  useEffect(() => {
    if (searchInput === query.searchString) return;

    const timer = setTimeout(
      () => setQuery((current) => ({ ...current, searchString: searchInput, pageNumber: 1 })),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchInput, query.searchString]);

  const fetchPage = useCallback(
    () =>
      contractsAPI.searchGroupContractMembers({
        searchGroupContractMembersCommand: {
          searchString: query.searchString || undefined,
          blockedOnly: query.blockedOnly,
          pageNumber: query.pageNumber,
          pageSize: PAGE_SIZE,
        },
      }),
    [query]
  );

  const { data, isLoading, error, reload } = useAsyncResource(fetchPage);

  const setBlockedOnly = useCallback(
    (blockedOnly: boolean) => setQuery((current) => ({ ...current, blockedOnly, pageNumber: 1 })),
    []
  );

  const setPageNumber = useCallback(
    (pageNumber: number) => setQuery((current) => ({ ...current, pageNumber })),
    []
  );

  return {
    data,
    isLoading,
    error,
    reload,
    query,
    searchInput,
    setSearchInput,
    setBlockedOnly,
    setPageNumber,
  };
};
